import { z } from "zod";
export const FLOW_MAX_NODES = 200;
export const FLOW_MAX_EDGES = 500;
export const FLOW_MAX_GRAPH_BYTES = 256 * 1024;
export const FLOW_MAX_EXECUTION_STEPS = 500;
export const META_INTERACTIVE_LIMITS = {
    body: 1024,
    header: 60,
    footer: 60,
    buttonCount: 3,
    buttonTitle: 20,
    buttonId: 256,
    listButton: 20,
    listSections: 10,
    listRows: 10,
    listSectionTitle: 24,
    listRowTitle: 24,
    listRowDescription: 72,
    listRowId: 200
};
export const flowStatusSchema = z.enum([
    "DRAFT",
    "PUBLISHED",
    "PAUSED",
    "ARCHIVED"
]);
export const flowVersionStatusSchema = z.enum(["DRAFT", "PUBLISHED"]);
export const flowExecutionStatusSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "WAITING",
    "COMPLETED",
    "FAILED",
    "CANCELLED",
    "TIMED_OUT",
    "HANDED_OFF"
]);
export const flowExecutionModeSchema = z.enum(["LIVE", "SIMULATION"]);
export const flowTriggerSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("MANUAL") }),
    z.object({ kind: z.literal("INBOUND_MESSAGE") }),
    z.object({
        kind: z.literal("KEYWORD"),
        value: z.string().trim().min(1).max(120),
        match: z.enum(["EXACT", "CONTAINS", "STARTS_WITH"]).default("EXACT")
    }),
    z.object({
        kind: z.literal("BUTTON_REPLY"),
        buttonId: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.buttonId)
    }),
    z.object({
        kind: z.literal("LIST_REPLY"),
        rowId: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.listRowId)
    }),
    z.object({ kind: z.literal("TICKET_CREATED") })
]);
export const flowNodeTypeSchema = z.enum([
    "START",
    "SEND_TEXT",
    "SEND_TEMPLATE",
    "SEND_BUTTONS",
    "SEND_LIST",
    "WAIT_DURATION",
    "WAIT_RESPONSE",
    "DECISION",
    "ASSIGN_QUEUE",
    "ASSIGN_USER",
    "ADD_TAG",
    "REMOVE_TAG",
    "UPDATE_TICKET",
    "HANDOFF",
    "END"
]);
const nodeBaseSchema = z.object({
    id: z.string().trim().min(1).max(96).regex(/^[A-Za-z0-9_-]+$/),
    name: z.string().trim().min(1).max(100),
    position: z.object({
        x: z.number().finite().min(-100_000).max(100_000),
        y: z.number().finite().min(-100_000).max(100_000)
    })
});
const errorPolicySchema = z
    .object({
    maxAttempts: z.number().int().min(1).max(10).default(3),
    backoffSeconds: z.number().int().min(1).max(3600).default(5)
})
    .default({ maxAttempts: 3, backoffSeconds: 5 });
const startNodeSchema = nodeBaseSchema.extend({
    type: z.literal("START"),
    config: z.object({ trigger: flowTriggerSchema })
});
const sendTextNodeSchema = nodeBaseSchema.extend({
    type: z.literal("SEND_TEXT"),
    config: z.object({
        text: z.string().trim().min(1).max(4096),
        errorPolicy: errorPolicySchema
    })
});
const sendTemplateNodeSchema = nodeBaseSchema.extend({
    type: z.literal("SEND_TEMPLATE"),
    config: z.object({
        templateId: z.string().trim().min(1).max(128),
        parameters: z.record(z.string().max(80), z.string().max(1024)).default({}),
        errorPolicy: errorPolicySchema
    })
});
const interactiveButtonSchema = z.object({
    id: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.buttonId),
    title: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.buttonTitle)
});
const sendButtonsNodeSchema = nodeBaseSchema.extend({
    type: z.literal("SEND_BUTTONS"),
    config: z.object({
        header: z.string().trim().max(META_INTERACTIVE_LIMITS.header).default(""),
        body: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.body),
        footer: z.string().trim().max(META_INTERACTIVE_LIMITS.footer).default(""),
        buttons: z
            .array(interactiveButtonSchema)
            .min(1)
            .max(META_INTERACTIVE_LIMITS.buttonCount),
        timeoutSeconds: z.number().int().min(1).max(7 * 24 * 60 * 60).default(3600),
        errorPolicy: errorPolicySchema
    })
});
const listRowSchema = z.object({
    id: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.listRowId),
    title: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.listRowTitle),
    description: z
        .string()
        .trim()
        .max(META_INTERACTIVE_LIMITS.listRowDescription)
        .default("")
});
const listSectionSchema = z.object({
    title: z.string().trim().max(META_INTERACTIVE_LIMITS.listSectionTitle).default(""),
    rows: z.array(listRowSchema).min(1).max(META_INTERACTIVE_LIMITS.listRows)
});
const sendListNodeSchema = nodeBaseSchema.extend({
    type: z.literal("SEND_LIST"),
    config: z
        .object({
        header: z.string().trim().max(META_INTERACTIVE_LIMITS.header).default(""),
        body: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.body),
        footer: z.string().trim().max(META_INTERACTIVE_LIMITS.footer).default(""),
        buttonText: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.listButton),
        sections: z.array(listSectionSchema).min(1).max(META_INTERACTIVE_LIMITS.listSections),
        timeoutSeconds: z.number().int().min(1).max(7 * 24 * 60 * 60).default(3600),
        errorPolicy: errorPolicySchema
    })
        .superRefine((value, context) => {
        const rows = value.sections.flatMap((section) => section.rows);
        if (rows.length > META_INTERACTIVE_LIMITS.listRows) {
            context.addIssue({
                code: "custom",
                path: ["sections"],
                message: `A lista pode ter no máximo ${META_INTERACTIVE_LIMITS.listRows} itens no total.`
            });
        }
        if (value.sections.length > 1 && value.sections.some((section) => !section.title)) {
            context.addIssue({
                code: "custom",
                path: ["sections"],
                message: "O título é obrigatório quando há mais de uma seção."
            });
        }
    })
});
const waitDurationNodeSchema = nodeBaseSchema.extend({
    type: z.literal("WAIT_DURATION"),
    config: z.object({
        value: z.number().int().min(1).max(365),
        unit: z.enum(["SECONDS", "MINUTES", "HOURS", "DAYS"])
    })
});
const waitResponseNodeSchema = nodeBaseSchema.extend({
    type: z.literal("WAIT_RESPONSE"),
    config: z.object({
        responseType: z.enum(["ANY", "TEXT", "BUTTON", "LIST"]),
        matcher: z.string().trim().max(256).default(""),
        timeoutSeconds: z.number().int().min(1).max(7 * 24 * 60 * 60).default(3600),
        handoffOnUnexpected: z.boolean().default(false)
    })
});
export const flowConditionOperatorSchema = z.enum([
    "EQUALS",
    "NOT_EQUALS",
    "CONTAINS",
    "NOT_CONTAINS",
    "STARTS_WITH",
    "EXISTS",
    "NOT_EXISTS",
    "GREATER_THAN",
    "LESS_THAN",
    "IN"
]);
const decisionConditionSchema = z.object({
    id: z.string().trim().min(1).max(96).regex(/^[A-Za-z0-9_-]+$/),
    label: z.string().trim().min(1).max(80),
    field: z.string().trim().min(1).max(120),
    operator: flowConditionOperatorSchema,
    value: z.union([z.string().max(1024), z.number(), z.boolean(), z.array(z.string().max(256)).max(50)]).optional()
});
const decisionNodeSchema = nodeBaseSchema.extend({
    type: z.literal("DECISION"),
    config: z.object({
        mode: z.enum(["ALL", "ANY"]).default("ALL"),
        conditions: z.array(decisionConditionSchema).min(1).max(20)
    })
});
const assignQueueNodeSchema = nodeBaseSchema.extend({
    type: z.literal("ASSIGN_QUEUE"),
    config: z.object({ queueId: z.string().trim().min(1).max(128) })
});
const assignUserNodeSchema = nodeBaseSchema.extend({
    type: z.literal("ASSIGN_USER"),
    config: z.object({ userId: z.string().trim().min(1).max(128) })
});
const tagActionConfigSchema = z.object({
    tagId: z.string().trim().min(1).max(128),
    target: z.enum(["CONTACT", "TICKET"]).default("CONTACT")
});
const addTagNodeSchema = nodeBaseSchema.extend({
    type: z.literal("ADD_TAG"),
    config: tagActionConfigSchema
});
const removeTagNodeSchema = nodeBaseSchema.extend({
    type: z.literal("REMOVE_TAG"),
    config: tagActionConfigSchema
});
const updateTicketNodeSchema = nodeBaseSchema.extend({
    type: z.literal("UPDATE_TICKET"),
    config: z
        .object({
        status: z.enum(["WAITING", "OPEN", "PENDING", "RESOLVED"]).optional(),
        queueId: z.string().trim().min(1).max(128).optional(),
        assignedUserId: z.string().trim().min(1).max(128).nullable().optional()
    })
        .refine((value) => value.status !== undefined ||
        value.queueId !== undefined ||
        value.assignedUserId !== undefined, { message: "Informe ao menos uma alteração do ticket." })
});
const handoffNodeSchema = nodeBaseSchema.extend({
    type: z.literal("HANDOFF"),
    config: z.object({
        queueId: z.string().trim().min(1).max(128).optional(),
        assignedUserId: z.string().trim().min(1).max(128).optional(),
        note: z.string().trim().max(500).default("")
    })
});
const endNodeSchema = nodeBaseSchema.extend({
    type: z.literal("END"),
    config: z.object({
        outcome: z.enum(["COMPLETED", "CANCELLED", "TIMED_OUT", "FAILED"]).default("COMPLETED")
    })
});
export const flowNodeSchema = z.discriminatedUnion("type", [
    startNodeSchema,
    sendTextNodeSchema,
    sendTemplateNodeSchema,
    sendButtonsNodeSchema,
    sendListNodeSchema,
    waitDurationNodeSchema,
    waitResponseNodeSchema,
    decisionNodeSchema,
    assignQueueNodeSchema,
    assignUserNodeSchema,
    addTagNodeSchema,
    removeTagNodeSchema,
    updateTicketNodeSchema,
    handoffNodeSchema,
    endNodeSchema
]);
export const flowEdgeSchema = z.object({
    id: z.string().trim().min(1).max(128).regex(/^[A-Za-z0-9_:-]+$/),
    source: z.string().trim().min(1).max(96),
    target: z.string().trim().min(1).max(96),
    sourceHandle: z.string().trim().min(1).max(300).default("success"),
    label: z.string().trim().max(80).default("")
});
export const flowGraphSchema = z.object({
    schemaVersion: z.literal(1).default(1),
    nodes: z.array(flowNodeSchema).min(1).max(FLOW_MAX_NODES),
    edges: z.array(flowEdgeSchema).max(FLOW_MAX_EDGES),
    viewport: z
        .object({ x: z.number().finite(), y: z.number().finite(), zoom: z.number().min(0.1).max(4) })
        .default({ x: 0, y: 0, zoom: 1 })
});
export const FLOW_IMPORT_FORMAT = "htzap-flow@1";
export const flowImportDocumentSchema = z.object({
    format: z.literal(FLOW_IMPORT_FORMAT).default(FLOW_IMPORT_FORMAT),
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(500).default(""),
    graph: flowGraphSchema
});
export const FLOW_VARIABLES = [
    "contact.name",
    "contact.phone",
    "contact.email",
    "contact.company.id",
    "contact.company.name",
    "contact.tags",
    "ticket.id",
    "ticket.protocol",
    "ticket.status",
    "ticket.queue.id",
    "ticket.queue.name",
    "ticket.tags",
    "assignedUser.id",
    "assignedUser.name",
    "message.text",
    "message.choice.id",
    "message.choice.title",
    "flow.execution.id"
];
const flowRuntimeValueSchema = z.union([
    z.string().max(2048),
    z.number(),
    z.boolean(),
    z.null()
]);
const flowRuntimeVariablesSchema = z
    .record(z.string().max(120), flowRuntimeValueSchema)
    .superRefine((variables, context) => {
    for (const key of Object.keys(variables)) {
        if (!FLOW_VARIABLES.includes(key)) {
            context.addIssue({
                code: "custom",
                path: [key],
                message: `A variável ${key} não está disponível no fluxo.`
            });
        }
    }
});
const variableExpression = /{{\s*([A-Za-z][A-Za-z0-9_.]*)\s*}}/g;
function issue(code, message, severity, references = {}) {
    return { code, message, severity, ...references };
}
function outgoingHandles(node) {
    switch (node.type) {
        case "END":
            return [];
        case "HANDOFF":
            return ["error"];
        case "SEND_BUTTONS":
            return [
                ...node.config.buttons.map((button) => `choice:${button.id}`),
                "unexpected",
                "timeout",
                "error"
            ];
        case "SEND_LIST":
            return [
                ...node.config.sections.flatMap((section) => section.rows.map((row) => `choice:${row.id}`)),
                "unexpected",
                "timeout",
                "error"
            ];
        case "WAIT_RESPONSE":
            return ["response", "unexpected", "timeout"];
        case "DECISION":
            return [
                ...node.config.conditions.map((condition) => `branch:${condition.id}`),
                "fallback"
            ];
        case "START":
            return ["success"];
        default:
            return ["success", "error"];
    }
}
function requiredHandles(node) {
    if (node.type === "END")
        return [];
    if (node.type === "HANDOFF")
        return [];
    if (node.type === "SEND_BUTTONS" || node.type === "SEND_LIST") {
        return outgoingHandles(node).filter((handle) => handle !== "error");
    }
    if (node.type === "WAIT_RESPONSE")
        return ["response", "unexpected", "timeout"];
    if (node.type === "DECISION")
        return outgoingHandles(node);
    if (node.type === "START")
        return ["success"];
    return ["success"];
}
function isDurableNode(node) {
    return ["WAIT_DURATION", "WAIT_RESPONSE", "SEND_BUTTONS", "SEND_LIST"].includes(node.type);
}
function stringsIn(value) {
    if (typeof value === "string")
        return [value];
    if (Array.isArray(value))
        return value.flatMap(stringsIn);
    if (value && typeof value === "object") {
        return Object.values(value).flatMap(stringsIn);
    }
    return [];
}
export function validateFlowGraph(input) {
    const parsed = flowGraphSchema.safeParse(input);
    if (!parsed.success) {
        const errors = parsed.error.issues.map((entry) => issue("INVALID_GRAPH", `${entry.path.join(".") || "grafo"}: ${entry.message}`, "ERROR", typeof entry.path[1] === "number" && input && typeof input === "object"
            ? {
                nodeId: String(input.nodes?.[entry.path[1]]
                    ?.id || "") || undefined
            }
            : {}));
        return { valid: false, errors, warnings: [] };
    }
    const graph = parsed.data;
    const errors = [];
    const warnings = [];
    const graphBytes = new TextEncoder().encode(JSON.stringify(graph)).byteLength;
    if (graphBytes > FLOW_MAX_GRAPH_BYTES) {
        errors.push(issue("GRAPH_TOO_LARGE", `O grafo excede o limite de ${FLOW_MAX_GRAPH_BYTES / 1024} KB.`, "ERROR"));
    }
    const nodeIds = new Set();
    const edgeIds = new Set();
    for (const node of graph.nodes) {
        if (nodeIds.has(node.id)) {
            errors.push(issue("DUPLICATE_NODE_ID", "Há nós com o mesmo identificador.", "ERROR", { nodeId: node.id }));
        }
        nodeIds.add(node.id);
    }
    for (const edge of graph.edges) {
        if (edgeIds.has(edge.id)) {
            errors.push(issue("DUPLICATE_EDGE_ID", "Há conexões com o mesmo identificador.", "ERROR", { edgeId: edge.id }));
        }
        edgeIds.add(edge.id);
        if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
            errors.push(issue("INVALID_EDGE", "A conexão aponta para um nó inexistente.", "ERROR", { edgeId: edge.id }));
        }
        if (edge.source === edge.target) {
            errors.push(issue("SELF_LOOP", "Um nó não pode conectar diretamente a si mesmo.", "ERROR", { nodeId: edge.source, edgeId: edge.id }));
        }
    }
    const starts = graph.nodes.filter((node) => node.type === "START");
    if (starts.length !== 1) {
        errors.push(issue("START_COUNT", "O fluxo precisa ter exatamente um nó Início.", "ERROR"));
    }
    if (!graph.nodes.some((node) => node.type === "END")) {
        errors.push(issue("MISSING_END", "O fluxo precisa ter ao menos um encerramento.", "ERROR"));
    }
    for (const node of graph.nodes) {
        const edges = graph.edges.filter((edge) => edge.source === node.id);
        const allowed = new Set(outgoingHandles(node));
        for (const edge of edges) {
            if (!allowed.has(edge.sourceHandle)) {
                errors.push(issue("INCOMPATIBLE_HANDLE", `A saída “${edge.sourceHandle}” não existe neste tipo de nó.`, "ERROR", { nodeId: node.id, edgeId: edge.id }));
            }
        }
        for (const handle of requiredHandles(node)) {
            if (!edges.some((edge) => edge.sourceHandle === handle)) {
                errors.push(issue("MISSING_CONNECTION", `A saída “${handle}” precisa ser conectada.`, "ERROR", { nodeId: node.id }));
            }
        }
        const seenHandles = new Set();
        for (const edge of edges) {
            if (seenHandles.has(edge.sourceHandle)) {
                errors.push(issue("AMBIGUOUS_CONNECTION", `A saída “${edge.sourceHandle}” possui mais de um destino.`, "ERROR", { nodeId: node.id, edgeId: edge.id }));
            }
            seenHandles.add(edge.sourceHandle);
        }
        const choiceIds = node.type === "SEND_BUTTONS"
            ? node.config.buttons.map((button) => button.id)
            : node.type === "SEND_LIST"
                ? node.config.sections.flatMap((section) => section.rows.map((row) => row.id))
                : [];
        if (new Set(choiceIds).size !== choiceIds.length) {
            errors.push(issue("DUPLICATE_CHOICE_ID", "As opções precisam ter identificadores únicos.", "ERROR", { nodeId: node.id }));
        }
        if (node.type === "DECISION") {
            const conditionIds = node.config.conditions.map((condition) => condition.id);
            if (new Set(conditionIds).size !== conditionIds.length) {
                errors.push(issue("DUPLICATE_CONDITION_ID", "As condições precisam ter identificadores únicos.", "ERROR", { nodeId: node.id }));
            }
            for (const condition of node.config.conditions) {
                if (!FLOW_VARIABLES.includes(condition.field)) {
                    errors.push(issue("UNSUPPORTED_CONDITION_FIELD", `O campo de decisão “${condition.field}” não está disponível.`, "ERROR", { nodeId: node.id }));
                }
            }
        }
        if (["SEND_TEXT", "SEND_BUTTONS", "SEND_LIST"].includes(node.type)) {
            warnings.push(issue("SERVICE_WINDOW_REQUIRED", "Esta mensagem livre só será enviada com a janela de 24 horas aberta.", "WARNING", { nodeId: node.id }));
        }
        for (const text of stringsIn(node.config)) {
            for (const match of text.matchAll(variableExpression)) {
                if (!FLOW_VARIABLES.includes(match[1])) {
                    errors.push(issue("UNSUPPORTED_VARIABLE", `A variável {{${match[1]}}} não está disponível.`, "ERROR", { nodeId: node.id }));
                }
            }
        }
    }
    if (starts[0]) {
        const reachable = new Set();
        const pending = [starts[0].id];
        while (pending.length) {
            const current = pending.pop();
            if (reachable.has(current))
                continue;
            reachable.add(current);
            for (const edge of graph.edges) {
                if (edge.source === current && !reachable.has(edge.target))
                    pending.push(edge.target);
            }
        }
        for (const node of graph.nodes) {
            if (!reachable.has(node.id)) {
                errors.push(issue("UNREACHABLE_NODE", "Este nó não pode ser alcançado a partir do início.", "ERROR", { nodeId: node.id }));
            }
        }
    }
    const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
    const visiting = new Set();
    const visited = new Set();
    const path = [];
    const reportedCycles = new Set();
    const walk = (nodeId) => {
        if (visiting.has(nodeId)) {
            const cycleStart = path.indexOf(nodeId);
            const cycle = path.slice(cycleStart);
            const signature = [...cycle].sort().join(":");
            if (!reportedCycles.has(signature) &&
                !cycle.some((id) => {
                    const node = nodeById.get(id);
                    return node ? isDurableNode(node) : false;
                })) {
                reportedCycles.add(signature);
                errors.push(issue("INSTANT_CYCLE", "O ciclo precisa conter uma espera durável para evitar execução infinita.", "ERROR", { nodeId }));
            }
            return;
        }
        if (visited.has(nodeId))
            return;
        visiting.add(nodeId);
        path.push(nodeId);
        for (const edge of graph.edges.filter((candidate) => candidate.source === nodeId)) {
            walk(edge.target);
        }
        path.pop();
        visiting.delete(nodeId);
        visited.add(nodeId);
    };
    if (starts[0])
        walk(starts[0].id);
    return { valid: errors.length === 0, errors, warnings };
}
export function createDefaultFlowGraph() {
    return {
        schemaVersion: 1,
        nodes: [
            {
                id: "start",
                type: "START",
                name: "Início",
                position: { x: 80, y: 160 },
                config: { trigger: { kind: "MANUAL" } }
            },
            {
                id: "end",
                type: "END",
                name: "Fluxo concluído",
                position: { x: 430, y: 160 },
                config: { outcome: "COMPLETED" }
            }
        ],
        edges: [
            {
                id: "edge-start-end",
                source: "start",
                target: "end",
                sourceHandle: "success",
                label: "Sucesso"
            }
        ],
        viewport: { x: 0, y: 0, zoom: 1 }
    };
}
export const createFlowSchema = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(500).default(""),
    graph: flowGraphSchema.optional()
});
export const updateFlowSchema = z
    .object({
    expectedVersion: z.number().int().nonnegative(),
    name: z.string().trim().min(2).max(120).optional(),
    description: z.string().trim().max(500).optional(),
    graph: flowGraphSchema.optional()
})
    .refine((value) => value.name !== undefined ||
    value.description !== undefined ||
    value.graph !== undefined, { message: "Informe ao menos uma alteração." });
export const flowExpectedVersionSchema = z.object({
    expectedVersion: z.number().int().nonnegative()
});
export const restoreFlowVersionSchema = flowExpectedVersionSchema.extend({
    flowVersionId: z.string().trim().min(1).max(128)
});
export const flowListQuerySchema = z.object({
    search: z.string().trim().max(120).default(""),
    status: flowStatusSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25)
});
export const flowExecutionListQuerySchema = z.object({
    flowId: z.string().trim().max(128).optional(),
    flowVersionId: z.string().trim().max(128).optional(),
    contactId: z.string().trim().max(128).optional(),
    ticketId: z.string().trim().max(128).optional(),
    nodeId: z.string().trim().max(96).optional(),
    failureCode: z.string().trim().max(120).optional(),
    status: flowExecutionStatusSchema.optional(),
    mode: flowExecutionModeSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25)
});
export const startFlowSimulationSchema = z.object({
    variables: flowRuntimeVariablesSchema.default({}),
    initialMessage: z.string().trim().max(4096).default("")
});
export const startFlowExecutionSchema = z.object({
    contactId: z.string().trim().min(1).max(128),
    ticketId: z.string().trim().min(1).max(128),
    idempotencyKey: z.string().trim().min(12).max(160),
    variables: flowRuntimeVariablesSchema.default({})
});
export const flowSimulationInputSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("MESSAGE"), text: z.string().trim().min(1).max(4096) }),
    z.object({
        type: z.literal("BUTTON"),
        id: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.buttonId),
        title: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.buttonTitle)
    }),
    z.object({
        type: z.literal("LIST"),
        id: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.listRowId),
        title: z.string().trim().min(1).max(META_INTERACTIVE_LIMITS.listRowTitle)
    }),
    z.object({ type: z.literal("TIME_ADVANCE"), seconds: z.number().int().min(1).max(365 * 24 * 60 * 60) }),
    z.object({ type: z.literal("META_FAILURE"), code: z.string().trim().min(1).max(80), message: z.string().trim().min(1).max(500) })
]);
export const FLOW_QUEUE_NAME = "flow-executions";
export const FLOW_JOB_ATTEMPTS = 8;
